import { useMutation, useQuery } from "@apollo/client";
import { ContentNode, Mutation, Query } from "@/app/graphql/codegen";
import { SELECT_CONTENT_NODE } from "@chat/graphql/mutations/selectContentNode";
import { FETCH_CONTEXT_FOR_CHAT } from "@chat/graphql/queries/fetchContextForChat";
import { useDispatch } from "react-redux";
import { triggerNotification } from "@store/slices/notification";

type SelectContentNodeOptions = {
  showNotifications?: boolean;
  additionalRefetchQueries?: any[];
};

/**
 * Hook to handle content node selection in chat with centralized functionality
 * 
 * @param chatId - The ID of the current chat
 * @param options - Configuration options
 * @returns Object containing the selectContentNode function and loading state
 */
export const useSelectContentNodeInChat = (
  chatId: string | undefined,
  options: SelectContentNodeOptions = {}
) => {
  const { 
    showNotifications = true,
    additionalRefetchQueries = [] 
  } = options;
  
  const dispatch = useDispatch();

  // Fetch current context to check for capacity limits
  const { data: contextData } = useQuery<Pick<Query, "contextForChat">>(
    FETCH_CONTEXT_FOR_CHAT,
    {
      variables: { chatId },
      skip: !chatId,
      fetchPolicy: "cache-first" // Use cache to avoid unnecessary network requests
    }
  );

  const [selectContentNodeMutation, { loading }] = useMutation<
    Pick<Mutation, "selectContentNode">
  >(SELECT_CONTENT_NODE, {
    refetchQueries: [
      { query: FETCH_CONTEXT_FOR_CHAT, variables: { chatId } },
      ...additionalRefetchQueries
    ],
    onError: (error) => {
      console.error("Error selecting content node:", error.message);

      // Check for context full error
      if (error.message.includes("context is full") || 
          error.message.includes("context limit") || 
          error.message.includes("exceeds maximum")) {
        
        if (showNotifications) {
          dispatch(
            triggerNotification({
              content: "Kontext ist voll, bitte ein oder mehrere Gesetze abwählen",
              type: "error",
              autoHideDuration: 5000,
            })
          );
        }
      } else {
        // General error handling
        if (showNotifications) {
          dispatch(
            triggerNotification({
              content: `Fehler beim Auswählen des Dokuments: ${error.message}`,
              type: "error",
              autoHideDuration: 3000,
            })
          );
        }
      }
    }
  });

  /**
   * Check if adding a document would exceed the context limit
   * @returns true if the context is already at or over capacity
   */
  const isContextFull = (): boolean => {
    const context = contextData?.contextForChat;
    if (!context) return false;

    const sizeInPages = context.sizeInPages || 0;
    const totalAllowedPages = context.totalAllowedPages || 0;
    
    if (totalAllowedPages === 0) return false;
    
    // Calculate current usage percentage
    const usagePercentage = Math.ceil((100 * sizeInPages) / totalAllowedPages);
    
    // Return true if already at or over 100%
    return usagePercentage >= 100;
  }

  /**
   * Check if a content node is already selected in the current context
   * @param contentNodeId - ID of the content node to check
   * @returns true if the node is already selected
   */
  const isContentNodeSelected = (contentNodeId: string): boolean => {
    const context = contextData?.contextForChat;
    if (!context || !context.contentNodes) return false;
    
    return context.contentNodes.some(node => node?.id === contentNodeId);
  }

  /**
   * Get reference of a document by its content node ID
   * @param contentNodeId - ID of the content node
   * @returns the document reference or undefined if not found
   */
  const getDocumentReference = (contentNodeId: string): string | undefined => {
    const context = contextData?.contextForChat;
    if (!context || !context.contentNodes) return undefined;
    
    const node = context.contentNodes.find(node => node?.id === contentNodeId);
    return node?.associatedOfficialDocument?.reference || undefined;
  }

  /**
   * Select or deselect a content node for the current chat
   * 
   * @param contentNodeId - ID of the content node to select/deselect
   * @param select - Whether to select (true) or deselect (false) the node
   * @param notificationMessage - Custom notification message to show on success (if undefined, no notification is shown)
   * @returns Promise resolving to the mutation result
   */
  const selectContentNode = async (
    contentNodeId: string,
    select: boolean = true,
    notificationMessage?: string
  ) => {
    if (!chatId) {
      console.error("No chat ID available");
      if (showNotifications) {
        dispatch(
          triggerNotification({
            content: "Fehler: Chat ID nicht verfügbar",
            type: "error",
            autoHideDuration: 3000,
          })
        );
      }
      return null;
    }

    // Special case: If trying to select a document that's already selected
    if (select && isContentNodeSelected(contentNodeId)) {
      const documentRef = getDocumentReference(contentNodeId);
      if (showNotifications) {
        dispatch(
          triggerNotification({
            content: `${documentRef || 'Dokument'} ist bereits ausgewählt!`,
            type: "info",
            autoHideDuration: 2000,
          })
        );
      }
      return null;
    }

    // If selecting (not deselecting) and context is already full, show error and don't proceed
    if (select && isContextFull()) {
      if (showNotifications) {
        dispatch(
          triggerNotification({
            content: "Kontext ist voll, bitte ein oder mehrere Gesetze abwählen",
            type: "error",
            autoHideDuration: 5000,
          })
        );
      }
      return null;
    }

    try {
      const result = await selectContentNodeMutation({
        variables: {
          chatId,
          contentNodeId,
          select,
        },
      });

      if (showNotifications && notificationMessage) {
        dispatch(
          triggerNotification({
            content: notificationMessage,
            type: "success",
            autoHideDuration: 2000,
          })
        );
      }

      return result;
    } catch (error) {
      // Error is already handled in the mutation config
      return null;
    }
  };

  return {
    selectContentNode,
    loading,
    isContextFull,
    isContentNodeSelected
  };
};